const STYLE = `
@media (prefers-reduced-motion: no-preference) {
  .gcl-float { animation: gcl-float 5s ease-in-out infinite; }
  .gcl-dash { stroke-dasharray: 6 8; animation: gcl-dash 3s linear infinite; }
}
@keyframes gcl-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes gcl-dash { to { stroke-dashoffset: -28; } }
`;

function Defs({ id }) {
  return (
    <>
      <style>{STYLE}</style>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--primary)" />
          <stop offset="1" stopColor="var(--highlight)" />
        </linearGradient>
      </defs>
    </>
  );
}

function Tv({ x, y, s, i }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <g className="gcl-float" style={{ animationDelay: `${i * 0.6}s` }}>
        <rect x="0" y="0" width="120" height="86" rx="10" />
        <rect x="10" y="10" width="100" height="66" rx="6" opacity=".6" />
        <line x1="40" y1="-22" x2="58" y2="0" />
        <line x1="80" y1="-22" x2="62" y2="0" />
        <line x1="30" y1="86" x2="24" y2="98" />
        <line x1="90" y1="86" x2="96" y2="98" />
        {[22, 34, 46, 58].map((yy) => (
          <line key={yy} x1="16" y1={yy} x2="104" y2={yy} opacity=".18" className="gcl-dash" />
        ))}
      </g>
    </g>
  );
}

/** Stack of wireframe TVs with a ghost peeking out. */
export function HauntedTVs({ className = '' }) {
  return (
    <svg viewBox="0 0 360 360" className={className} fill="none" stroke="url(#gclTv)" strokeWidth="1.3" aria-hidden="true">
      <Defs id="gclTv" />
      <Tv x={40} y={210} s={1} i={0} />
      <Tv x={190} y={220} s={0.9} i={1} />
      <Tv x={115} y={90} s={1.05} i={2} />
      <g transform="translate(150 118)">
        <g className="gcl-float" style={{ animationDuration: '4s' }}>
          <path
            d="M0 40 a24 24 0 0 1 48 0 v26 l-8 -6 -8 6 -8 -6 -8 6 -8 -6 -8 6z"
            stroke="var(--primary)"
            fill="var(--primary)"
            fillOpacity="0.08"
          />
          <ellipse cx="16" cy="38" rx="3" ry="5" fill="var(--primary)" stroke="none" />
          <ellipse cx="32" cy="38" rx="3" ry="5" fill="var(--primary)" stroke="none" />
        </g>
      </g>
    </svg>
  );
}

const CELLS = [
  [180, 70, 34],
  [130, 130, 30],
  [230, 130, 30],
  [180, 190, 40],
  [100, 210, 22],
  [260, 210, 22],
  [130, 270, 30],
  [230, 270, 30],
  [180, 315, 18],
  [70, 140, 12],
  [290, 140, 12],
  [180, 130, 10],
];

function hexPoints(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');
}

/** Interlocked hex cluster: the on-demand vault. */
export function HexCluster({ className = '' }) {
  return (
    <svg viewBox="0 0 360 360" className={className} fill="none" stroke="url(#gclHex)" strokeWidth="1.2" aria-hidden="true">
      <Defs id="gclHex" />
      {CELLS.map(([x, y, r], k) => (
        <g
          key={`${x}-${y}`}
          className="gcl-float"
          style={{ animationDelay: `${(k % 5) * 0.5}s`, animationDuration: `${5 + (k % 3)}s` }}
        >
          <polygon points={hexPoints(x, y, r)} />
          <polygon points={hexPoints(x, y, r)} transform={`translate(${r * 0.18} ${-r * 0.18})`} opacity=".35" />
        </g>
      ))}
    </svg>
  );
}

/** Antenna emitting spectral waves: picture quality. */
export function SpectralSignal({ className = '' }) {
  return (
    <svg viewBox="0 0 360 360" className={className} fill="none" stroke="url(#gclSig)" strokeWidth="1.3" aria-hidden="true">
      <Defs id="gclSig" />
      <path d="M180 330 L150 200 L210 200 Z" />
      <line x1="165" y1="265" x2="195" y2="265" />
      <line x1="158" y1="232" x2="202" y2="232" />
      <circle cx="180" cy="186" r="10" stroke="var(--primary)" />
      {[40, 75, 110, 145].map((r, i) => (
        <path
          key={r}
          d={`M ${180 - r} ${186 - r * 0.25} A ${r} ${r} 0 0 1 ${180 + r} ${186 - r * 0.25}`}
          className="gcl-dash"
          style={{ animationDuration: `${2 + i}s` }}
          opacity={1 - i * 0.18}
        />
      ))}
    </svg>
  );
}

const DEVICE_PATHS = {
  tv: (
    <>
      <rect x="6" y="10" width="52" height="34" rx="3" />
      <line x1="22" y1="52" x2="42" y2="52" />
      <line x1="32" y1="44" x2="32" y2="52" />
    </>
  ),
  stick: (
    <>
      <rect x="10" y="26" width="36" height="12" rx="3" />
      <rect x="46" y="29" width="8" height="6" />
      <circle cx="18" cy="32" r="2" />
    </>
  ),
  phone: (
    <>
      <rect x="20" y="6" width="24" height="48" rx="4" />
      <line x1="29" y1="49" x2="35" y2="49" />
    </>
  ),
  tablet: (
    <>
      <rect x="12" y="8" width="40" height="48" rx="4" />
      <circle cx="32" cy="51" r="1.5" />
    </>
  ),
  laptop: (
    <>
      <rect x="12" y="14" width="40" height="28" rx="2" />
      <path d="M6 48 h52 l-4 -6 h-44z" />
    </>
  ),
  box: (
    <>
      <rect x="8" y="24" width="48" height="16" rx="3" />
      <circle cx="46" cy="32" r="2" />
      <line x1="14" y1="32" x2="30" y2="32" />
    </>
  ),
};

/** Small device icon for the supported-devices grid. kind: tv | stick | phone | tablet | laptop | box */
export function DeviceIcon({ kind, className = 'h-14 w-14' }) {
  const id = `gclDev-${kind}`;
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke={`url(#${id})`} strokeWidth="1.4" aria-hidden="true">
      <Defs id={id} />
      {DEVICE_PATHS[kind] ?? DEVICE_PATHS.tv}
    </svg>
  );
}

/** Three-part glyph above each value. variant: 0 | 1 | 2 */
export function TripleGlyph({ variant = 0 }) {
  const id = `gclGlyph-${variant}`;
  return (
    <svg viewBox="0 0 64 32" className="h-8 w-16" fill="none" stroke={`url(#${id})`} strokeWidth="1.3" aria-hidden="true">
      <Defs id={id} />
      {variant === 0 && (
        <>
          <path d="M14 4 L2 16 L14 28" />
          <rect x="22" y="7" width="13" height="13" transform="rotate(45 28.5 13.5)" />
          <path d="M50 4 L62 16 L50 28" />
        </>
      )}
      {variant === 1 && (
        <>
          <path d="M4 4 L16 16 L4 28" />
          <path d="M16 4 L28 16 L16 28" />
          <rect x="34" y="7" width="13" height="13" transform="rotate(45 40.5 13.5)" />
          <rect x="44" y="7" width="13" height="13" transform="rotate(45 50.5 13.5)" />
        </>
      )}
      {variant === 2 && (
        <>
          {[10, 22, 34].map((x) => (
            <rect key={x} x={x} y="7" width="13" height="13" transform={`rotate(45 ${x + 6.5} 13.5)`} />
          ))}
        </>
      )}
    </svg>
  );
}
